import React from "react";
import {
  ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight, Download, Loader2, Search, X,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { eksporCsv, eksporCsvServer, UKURAN_PILIHAN } from "../../hooks/useDaftar";

// PalangDaftar — satu palang di atas tabel: cari · saringan (children) · jumlah ·
// ekspor, lalu baris CHIP saringan aktif di bawahnya. Pasangannya `useDaftar()`;
// kepala kolom memakai `ThUrut`, kaki tabel memakai `Paginasi`. Tahap 2
// kematangan ERP (2 Sep 2026), diperluas jadi POLA DAFTAR SERAGAM 27 Sep 2026
// (`docs/POLA_DAFTAR.md`).
//
// Kenapa satu komponen dan bukan tiap halaman menulis sendiri: 31 halaman
// bertabel dengan lima gaya kepala yang berbeda adalah persis sebab sistem
// ini terasa "belum mapan". Odoo memakai SATU control panel di semua modul,
// dan mata belajar sekali.

function angka(n) {
  return Number(n || 0).toLocaleString("id-ID");
}

/** Chip saringan aktif. Selalu terlihat — saringan yang tersembunyi membuat orang
 *  mengira datanya hilang ("kok cuma 3?") padahal layar sedang menyaring. */
export function ChipSaringan({ daftar, testId = "palang-daftar" }) {
  const aktif = daftar.saringanAktif || [];
  if (aktif.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 -mt-1 mb-3" data-testid={`${testId}-chip`}>
      <span className="text-xs text-muted-foreground">Disaring:</span>
      {aktif.map((s) => (
        <span key={s.kunci}
              className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/50 pl-2.5 pr-1 py-0.5 text-xs"
              data-testid={`${testId}-chip-${s.kunci}`}>
          <span className="text-muted-foreground">{s.label}:</span>
          <span className="font-medium">{s.teks}</span>
          <button type="button"
                  className="ml-0.5 rounded-full p-0.5 hover:bg-muted"
                  onClick={() => daftar.hapusSaringan(s.kunci)}
                  aria-label={`Hapus saringan ${s.label}`}
                  title={`Hapus saringan ${s.label}`}
                  data-testid={`${testId}-chip-${s.kunci}-hapus`}>
            <X size={12} />
          </button>
        </span>
      ))}
      {aktif.length > 1 && (
        <button type="button" className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
                onClick={() => daftar.hapusSemuaSaringan()} data-testid={`${testId}-chip-hapus-semua`}>
          Hapus semua
        </button>
      )}
    </div>
  );
}

export function PalangDaftar({ daftar, placeholder = "Cari…", children, ekspor, testId = "palang-daftar" }) {
  const [sibuk, setSibuk] = React.useState(false);
  const [catatanEkspor, setCatatanEkspor] = React.useState(null);

  // ⛔ Di mode server, `daftar.terurut` cuma HALAMAN INI. Mengekspornya
  //    menghasilkan berkas yang tampak lengkap dan diam-diam kurang — jadi
  //    mode server WAJIB memberi `ambilHalaman`, dan kalau ia lupa diberikan
  //    tombolnya dimatikan, bukan diam-diam mengekspor sepotong.
  const eksporServer = daftar.modeServer && typeof ekspor?.ambilHalaman === "function";
  const eksporMati = !ekspor || daftar.total === 0 || sibuk
    || (daftar.modeServer && !eksporServer);

  const jalankanEkspor = async () => {
    if (!ekspor) return;
    setCatatanEkspor(null);
    if (!eksporServer) { eksporCsv(ekspor.nama, ekspor.kolom, daftar.terurut); return; }
    setSibuk(true);
    try {
      const hasil = await eksporCsvServer(ekspor.nama, ekspor.kolom, ekspor.ambilHalaman, daftar.total);
      // ⛔ Dulu tanda `terpotong` dibuang di sini: berkas 5.000 baris dari 6.480 tampak
      //    lengkap dan dipakai menghitung. Sekarang orangnya DIBERI TAHU, dua tempat.
      if (hasil?.terpotong) {
        const pesan = `Yang terunduh hanya ${angka(hasil.diekspor)} baris pertama dari ${angka(hasil.total)}. `
          + "Persempit saringan atau kata cari untuk mengunduh sisanya.";
        setCatatanEkspor(pesan);
        toast.warning(pesan);
      }
    } catch {
      toast.error("Unduhan gagal disiapkan. Coba lagi sebentar lagi.");
    } finally {
      setSibuk(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid={testId}>
        <div className="relative flex-1 min-w-[12rem] max-w-sm">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            value={daftar.cari}
            onChange={(e) => daftar.setCari(e.target.value)}
            placeholder={placeholder}
            className="pl-8 h-9"
            aria-label="Cari di daftar"
            data-testid={`${testId}-cari`}
          />
        </div>
        {children}
        <span className="ml-auto text-xs text-muted-foreground tabular-nums" data-testid={`${testId}-jumlah`}>
          {daftar.cari.trim() ? `${angka(daftar.total)} cocok` : `${angka(daftar.total)} baris`}
        </span>
        {ekspor && (
          <Button
            variant="outline" size="sm"
            disabled={eksporMati}
            onClick={jalankanEkspor}
            title={daftar.modeServer && !eksporServer
              ? "Unduhan belum tersedia di layar ini"
              : "Unduh seluruh hasil saringan sebagai berkas untuk Excel"}
            data-testid={`${testId}-ekspor`}
          >
            {sibuk
              ? <Loader2 size={14} className="mr-1 animate-spin" />
              : <Download size={14} className="mr-1" />}
            {sibuk ? "Menyiapkan…" : "Unduh CSV"}
          </Button>
        )}
      </div>
      {catatanEkspor && (
        <p className="-mt-1 mb-3 text-xs text-warning-soft-foreground" role="status"
           data-testid={`${testId}-ekspor-terpotong`}>
          {catatanEkspor}
        </p>
      )}
      <ChipSaringan daftar={daftar} testId={testId} />
    </>
  );
}

/** Kepala kolom yang bisa diklik untuk mengurut. `kunci` harus sama dengan
 *  nama field baris (atau `kolom[].kunci` dengan `nilai()` di useDaftar). */
export function ThUrut({ daftar, kunci, children, className = "", align = "left" }) {
  // Kolom yang server tidak bisa urutkan dirender sebagai kepala BIASA — tanpa
  // ikon, tanpa kursor, tanpa klik. Lebih baik tidak menawarkan daripada
  // menawarkan lalu diam-diam tidak mengerjakan.
  if (daftar.bolehUrut && !daftar.bolehUrut(kunci)) {
    return <th className={className} data-testid={`th-${kunci}`}>{children}</th>;
  }
  const aktif = daftar.urut?.kunci === kunci;
  const arah = aktif ? daftar.urut.arah : null;
  const Ikon = !aktif ? ArrowUpDown : arah === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      className={`${className} select-none cursor-pointer hover:text-foreground`}
      aria-sort={aktif ? (arah === "asc" ? "ascending" : "descending") : "none"}
      onClick={() => daftar.toggleUrut(kunci)}
      data-testid={`th-urut-${kunci}`}
      data-arah={arah || ""}
    >
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "flex-row-reverse" : ""}`}>
        {children}
        <Ikon size={12} className={aktif ? "opacity-100" : "opacity-40"} />
      </span>
    </th>
  );
}

/** Kaki tabel: "1–25 dari N" · baris per halaman · ‹ › — dan di layar sempit
 *  (< 640px) tombol "Muat lagi" yang MENAMBAHKAN halaman berikutnya ke daftar.
 *  Tidak tampil kalau semuanya muat di satu halaman terkecil — paginasi untuk
 *  tujuh baris cuma bising. */
export function Paginasi({ daftar, testId = "paginasi", pilihanUkuran = UKURAN_PILIHAN }) {
  const pilihan = [...new Set([...pilihanUkuran, daftar.ukuran])].sort((a, b) => a - b);
  if (daftar.total <= Math.min(...pilihan) && daftar.halaman <= 1) return null;
  const { awal, akhir } = daftar.rentang
    || { awal: (daftar.halaman - 1) * daftar.ukuran + 1, akhir: Math.min(daftar.total, daftar.halaman * daftar.ukuran) };
  const teksRentang = `${angka(awal)}–${angka(akhir)} dari ${angka(daftar.total)}`;
  return (
    <div className="border-t border-border px-3 py-2 text-xs text-muted-foreground" data-testid={testId}>
      {/* Layar lebar: pindah halaman */}
      <div className="hidden sm:flex items-center justify-between gap-2" data-testid={`${testId}-lebar`}>
        <span className="tabular-nums" data-testid={`${testId}-rentang`}>{teksRentang}</span>
        <div className="flex items-center gap-2">
          {daftar.setUkuran && (
            <label className="flex items-center gap-1">
              <select
                className="h-8 rounded-md border border-input bg-background px-1.5 text-xs text-foreground"
                value={daftar.ukuran}
                onChange={(e) => daftar.setUkuran(Number(e.target.value))}
                aria-label="Jumlah baris per halaman"
                data-testid={`${testId}-ukuran`}
              >
                {pilihan.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <span>per halaman</span>
            </label>
          )}
          <Button size="sm" variant="outline" className="h-8 w-8 p-0"
                  disabled={daftar.halaman <= 1}
                  onClick={() => daftar.setHalaman(daftar.halaman - 1)}
                  aria-label="Halaman sebelumnya" title="Halaman sebelumnya"
                  data-testid={`${testId}-prev`}>
            <ChevronLeft size={16} />
          </Button>
          <span className="tabular-nums" data-testid={`${testId}-halaman`}>
            Hal {angka(daftar.halaman)} dari {angka(daftar.totalHalaman)}
          </span>
          <Button size="sm" variant="outline" className="h-8 w-8 p-0"
                  disabled={daftar.halaman >= daftar.totalHalaman}
                  onClick={() => daftar.setHalaman(daftar.halaman + 1)}
                  aria-label="Halaman berikutnya" title="Halaman berikutnya"
                  data-testid={`${testId}-next`}>
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>
      {/* Layar sempit: muat lagi */}
      <div className="flex sm:hidden flex-col items-stretch gap-2" data-testid={`${testId}-sempit`}>
        <span className="text-center tabular-nums">Menampilkan {teksRentang}</span>
        {daftar.muatLagi && daftar.adaLagi && (
          <Button size="sm" variant="outline" onClick={() => daftar.muatLagi()}
                  data-testid={`${testId}-muat-lagi`}>
            Muat lagi
          </Button>
        )}
      </div>
    </div>
  );
}
